<template>
  <div class="breadcrumb-container">
    <!-- 返回按钮组 -->
    <div class="back-buttons-group">
      <!-- 返回上一级按钮 -->
      <button
        v-if="showBackButton"
        @click="handleGoBack"
        class="back-button back-previous"
      >
        <div class="button-content">
          <div class="icon-wrapper">
            <svg
              class="arrow-icon"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2.5"
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
          </div>
          <div class="text-wrapper">
            <span class="main-text">返回上一级</span>
            <span class="sub-text">Go Back</span>
          </div>
        </div>
      </button>

      <!-- 返回首页按钮 -->
      <router-link to="/" class="back-button back-home">
        <div class="button-content">
          <div class="icon-wrapper">
            <svg
              class="arrow-icon"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
              />
            </svg>
          </div>
          <div class="text-wrapper">
            <span class="main-text">返回首页</span>
            <span class="sub-text">Back to Home</span>
          </div>
        </div>
      </router-link>
    </div>

    <!-- 面包屑导航 -->
    <nav class="breadcrumb">
      <router-link to="/" class="breadcrumb-item">
        <svg
          class="breadcrumb-icon"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
          />
        </svg>
        <span>首页</span>
      </router-link>

      <span class="breadcrumb-separator">/</span>

      <router-link
        v-if="topicId && topicName"
        :to="`/topic/${topicId}`"
        class="breadcrumb-item"
      >
        <span class="breadcrumb-icon">{{ topicIcon }}</span>
        <span>{{ topicName }}</span>
      </router-link>

      <template v-if="topicId && topicName">
        <span class="breadcrumb-separator">/</span>
      </template>

      <span class="breadcrumb-current">{{ currentPage }}</span>
    </nav>
  </div>
</template>

<script>
import { computed } from "vue";
import { useRouter } from "vue-router";
import { getReferrer, clearReferrer } from "@/utils/navigationState";

export default {
  name: "Breadcrumb",
  props: {
    topicId: {
      type: String,
      default: "",
    },
    topicName: {
      type: String,
      default: "",
    },
    topicIcon: {
      type: String,
      default: "📝",
    },
    currentPage: {
      type: String,
      required: true,
    },
  },
  setup() {
    const router = useRouter();

    // 检查是否有来源页面
    const showBackButton = computed(() => {
      const referrer = getReferrer();
      return referrer && referrer !== "/";
    });

    const handleGoBack = () => {
      const referrer = getReferrer();
      if (referrer) {
        clearReferrer();
        router.push(referrer);
      } else {
        router.back();
      }
    };

    return {
      showBackButton,
      handleGoBack,
    };
  },
};
</script>

<style scoped>
.breadcrumb-container {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

/* 返回按钮组 */
.back-buttons-group {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

/* 返回按钮 */
.back-button {
  position: relative;
  display: inline-flex;
  align-items: center;
  padding: 0.625rem 1.25rem;
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.98),
    rgba(255, 255, 255, 0.95)
  );
  backdrop-filter: blur(12px);
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.06);
  border: 1px solid rgba(59, 130, 246, 0.1);
  text-decoration: none;
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
}

.back-button::before {
  content: "";
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.back-button:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.15);
  border-color: rgba(59, 130, 246, 0.2);
}

.back-button:hover::before {
  opacity: 0.05;
}

/* 返回上一级按钮特殊样式 */
.back-previous {
  border-color: rgba(139, 92, 246, 0.1);
}

.back-previous:hover {
  border-color: rgba(139, 92, 246, 0.2);
  box-shadow: 0 4px 12px rgba(139, 92, 246, 0.15);
}

.back-previous::before {
  background: linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%);
}

.button-content {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.625rem;
  z-index: 1;
}

.icon-wrapper {
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.arrow-icon {
  width: 16px;
  height: 16px;
  color: #6b7280;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.back-button:hover .arrow-icon {
  color: #3b82f6;
  transform: translateX(-3px);
}

.back-previous:hover .arrow-icon {
  color: #8b5cf6;
}

.text-wrapper {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.main-text {
  font-size: 0.875rem;
  font-weight: 600;
  color: #1f2937;
  transition: all 0.3s ease;
  line-height: 1.2;
}

.sub-text {
  font-size: 0.625rem;
  color: #9ca3af;
  font-weight: 500;
  letter-spacing: 0.03em;
  transition: all 0.3s ease;
  line-height: 1.2;
  margin-top: 2px;
}

.back-button:hover .main-text {
  color: #3b82f6;
}

.back-previous:hover .main-text {
  color: #8b5cf6;
}

.back-button:hover .sub-text {
  color: #8b5cf6;
}

.back-previous:hover .sub-text {
  color: #ec4899;
}

/* 面包屑导航 */
.breadcrumb {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 0;
  font-size: 13px;
  flex-wrap: wrap;
}

.breadcrumb-item {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: #6b7280;
  text-decoration: none;
  padding: 3px 6px;
  border-radius: 6px;
  transition: all 0.2s ease;
  position: relative;
}

.breadcrumb-item::before {
  content: "";
  position: absolute;
  inset: 0;
  background: linear-gradient(
    135deg,
    rgba(59, 130, 246, 0.08),
    rgba(139, 92, 246, 0.08)
  );
  opacity: 0;
  border-radius: 6px;
  transition: opacity 0.2s ease;
}

.breadcrumb-item:hover {
  color: #3b82f6;
}

.breadcrumb-item:hover::before {
  opacity: 1;
}

.breadcrumb-icon {
  width: 14px;
  height: 14px;
  flex-shrink: 0;
}

.breadcrumb-separator {
  color: #d1d5db;
  font-weight: 300;
  font-size: 12px;
}

.breadcrumb-current {
  color: #1f2937;
  font-weight: 600;
  padding: 3px 8px;
  background: linear-gradient(
    135deg,
    rgba(59, 130, 246, 0.06),
    rgba(139, 92, 246, 0.06)
  );
  border-radius: 6px;
  max-width: 400px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

@media (max-width: 640px) {
  .back-button {
    padding: 0.5rem 1rem;
  }

  .icon-wrapper {
    width: 24px;
    height: 24px;
  }

  .arrow-icon {
    width: 14px;
    height: 14px;
  }

  .main-text {
    font-size: 0.8125rem;
  }

  .sub-text {
    display: none;
  }

  .breadcrumb {
    font-size: 12px;
    gap: 5px;
    padding: 4px 0;
  }

  .breadcrumb-icon {
    width: 12px;
    height: 12px;
  }

  .breadcrumb-current {
    max-width: 200px;
    font-size: 11px;
  }
}
</style>
